import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MODULE_KEYS, MODULE_LABEL, MODULE_HINT, ORG_ROLE_LABEL } from '../../auth/capabilities';
import { VpState } from '../../components/VelopulseShell';
import useLoad from '../../utils/useLoad';
import { vpApi, errorMessage } from '../../api/velopulse';

const toggleModule = (list, key) => (
  list.includes(key) ? list.filter((k) => k !== key) : [...list, key]
);

const emptyAdd = { email: '', name: '', password: '', org_role: 'member' };

export default function PlatformCompanyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const companyLoad = useLoad(() => vpApi.getPlatformCompany(id), [id]);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const membersLoad = useLoad(
    () => vpApi.listPlatformCompanyMembers(id, { q, org_role: roleFilter, status: statusFilter }),
    [id, q, roleFilter, statusFilter],
  );

  const company = companyLoad.data;
  const members = Array.isArray(membersLoad.data) ? membersLoad.data : [];
  const [editModules, setEditModules] = useState(false);
  const [modules, setModules] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(emptyAdd);
  const [saving, setSaving] = useState(false);

  const openModules = () => {
    setModules(Array.isArray(company?.modules) ? [...company.modules] : []);
    setEditModules(true);
  };

  const saveModules = async (e) => {
    e.preventDefault();
    if (!modules.length) { toast.error('請至少選擇賣房或賣車其中一項'); return; }
    const labels = modules.map((k) => MODULE_LABEL[k] || k).join('、');
    const ok = window.confirm(`確定將「${company.name}」的功能更新為：${labels}？\n\n公司管理員只能查看，不能自己開關。`);
    if (!ok) return;
    setSaving(true);
    try {
      await vpApi.updatePlatformCompany(id, { modules });
      toast.success('功能已更新');
      setEditModules(false);
      companyLoad.reload();
    } catch (err) {
      toast.error(errorMessage(err, '無法更新功能'));
    } finally {
      setSaving(false);
    }
  };

  const handleActiveCompany = async () => {
    const next = company.is_active === false;
    const ok = window.confirm(next
      ? `確定啟用「${company.name}」？`
      : `確定停用「${company.name}」？成員將無法再進入，資料不會刪除。`);
    if (!ok) return;
    try {
      await vpApi.updatePlatformCompany(id, { is_active: next });
      toast.success(next ? '公司已啟用' : '公司已停用');
      companyLoad.reload();
    } catch (err) {
      toast.error(errorMessage(err, '無法變更公司狀態'));
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const email = addForm.email.trim();
    if (!email) { toast.error('請輸入 Email'); return; }
    const roleName = ORG_ROLE_LABEL[addForm.org_role] || addForm.org_role;
    const name = addForm.name.trim();
    const password = addForm.password;
    const ok = window.confirm(
      `確定將「${email}」以「${roleName}」加入「${company.name}」？\n\n若帳號尚不存在，將用你填的姓名與初始密碼建立登入帳號。不會授予平台管理員權限。`
    );
    if (!ok) return;
    setSaving(true);
    try {
      const result = await vpApi.addPlatformCompanyMember(id, {
        email,
        name: name || undefined,
        password: password || undefined,
        org_role: addForm.org_role,
      });
      toast.success(result?.user_created ? '已建立帳號並加入公司' : '成員已加入');
      setShowAdd(false);
      setAddForm(emptyAdd);
      membersLoad.reload();
      companyLoad.reload();
    } catch (err) {
      toast.error(errorMessage(err, '無法加入成員'));
    } finally {
      setSaving(false);
    }
  };

  const handleRole = async (m, org_role) => {
    if (org_role === m.org_role) return;
    if (m.role === 'super_admin') { toast.error('無法變更超級管理員的公司身分'); return; }
    const ok = window.confirm(
      `確定將「${m.name}」在「${company.name}」的角色改為「${ORG_ROLE_LABEL[org_role] || org_role}」？`
    );
    if (!ok) return;
    try {
      await vpApi.updatePlatformCompanyMember(id, m.id, { org_role });
      toast.success('公司角色已更新');
      membersLoad.reload();
    } catch (err) {
      toast.error(errorMessage(err, '無法更新角色'));
    }
  };

  const handleToggle = async (m) => {
    if (m.role === 'super_admin') { toast.error('無法變更超級管理員的公司身分'); return; }
    const next = m.is_active === false;
    const ok = window.confirm(next
      ? `確定恢復「${m.name}」在「${company.name}」的存取？`
      : `確定停止「${m.name}」在「${company.name}」的存取？\n\n只影響本公司 membership，不會停用全域登入，也不會授予／撤銷平台管理員。`);
    if (!ok) return;
    try {
      await vpApi.setPlatformCompanyMemberStatus(id, m.id, next);
      toast.success(next ? '已恢復本公司存取' : '已停止本公司存取');
      membersLoad.reload();
    } catch (err) {
      toast.error(errorMessage(err, '操作失敗'));
    }
  };

  if (companyLoad.loading && !company) {
    return <div className="vp-content"><VpState loading /></div>;
  }
  if (companyLoad.error || !company) {
    return (
      <div className="vp-content">
        <div className="vp-card">
          <VpState
            text={errorMessage(companyLoad.error, '找不到這家公司。')}
            action={{ label: '回公司清單', onClick: () => navigate('/platform') }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="vp-content" style={{ maxWidth: 1040 }}>
      <div className="vp-hello">
        <div>
          <p className="vp-muted" style={{ marginBottom: 4 }}>
            <Link to="/platform">← 公司清單</Link>
            <span> · 平台管理（非公司管理）</span>
          </p>
          <h1>{company.name}</h1>
          <p>
            {company.short_name}{company.code ? ` · ${company.code}` : ''}
            {company.industry ? ` · ${company.industry}` : ''}
            {' · '}
            {company.is_active === false ? '已停用' : '啟用中'}
            {' · '}
            成員 {company.member_count ?? members.length}
          </p>
        </div>
        <div className="vp-actions">
          <button type="button" className="vp-btn" onClick={openModules}>編輯功能</button>
          <button type="button" className={`vp-btn${company.is_active === false ? '' : ' danger'}`} onClick={handleActiveCompany}>
            {company.is_active === false ? '啟用公司' : '停用公司'}
          </button>
          <button type="button" className="vp-btn primary" onClick={() => { setAddForm(emptyAdd); setShowAdd(true); }}>加入成員</button>
        </div>
      </div>

      <div className="vp-card" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>已開通功能</h2>
        <div className="vp-module-badges">
          {(company.module_labels || []).length
            ? (company.module_labels || []).map((m) => <span key={m.key} className="vp-badge blue">{m.label}</span>)
            : <span className="vp-muted">尚未設定</span>}
        </div>
        <p className="vp-muted" style={{ marginTop: 8 }}>
          賣房＝稅試算／調謄本／搜房；賣車＝車輛紀錄／名片王／號牌查詢；商機需任一模組。
        </p>
      </div>

      <div className="vp-hello" style={{ marginBottom: 8 }}>
        <div>
          <h2 style={{ fontSize: 18 }}>成員與權限</h2>
          <p className="vp-muted">僅管理此公司的 org_role（公司管理員／團隊主管／成員），不會在此授予平台管理員。</p>
        </div>
      </div>

      <div className="vp-bar">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜尋姓名或 Email" aria-label="搜尋成員" />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} aria-label="角色篩選">
          <option value="">全部角色</option>
          <option value="org_admin">公司管理員</option>
          <option value="team_manager">團隊主管</option>
          <option value="member">一般成員</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="狀態篩選">
          <option value="all">全部狀態</option>
          <option value="active">啟用中</option>
          <option value="inactive">已停用（本公司）</option>
        </select>
      </div>

      {membersLoad.error ? (
        <div className="vp-card"><VpState text={errorMessage(membersLoad.error, '無法載入成員。')} action={{ label: '重新載入', onClick: membersLoad.reload }} /></div>
      ) : membersLoad.loading && !members.length ? (
        <div className="vp-card"><VpState loading /></div>
      ) : members.length === 0 ? (
        <div className="vp-card"><VpState text="還沒有成員。" action={{ label: '加入成員', onClick: () => setShowAdd(true) }} /></div>
      ) : (
        <div className="vp-list-card">
          <table className="vp-table">
            <thead>
              <tr>
                {['姓名', 'Email', '公司角色', '狀態', '操作'].map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}{m.role === 'super_admin' ? ' · 超級管理員' : ''}</td>
                  <td>{m.email}</td>
                  <td>
                    <select
                      className="vp-input"
                      style={{ minWidth: 140, padding: '6px 8px' }}
                      value={m.org_role || 'member'}
                      disabled={m.role === 'super_admin'}
                      onChange={(e) => handleRole(m, e.target.value)}
                    >
                      <option value="member">一般成員</option>
                      <option value="team_manager">團隊主管</option>
                      <option value="org_admin">公司管理員</option>
                    </select>
                  </td>
                  <td>{m.is_active === false ? '已停用（本公司）' : '啟用中'}</td>
                  <td>
                    <button
                      type="button"
                      className={`vp-btn${m.is_active === false ? '' : ' danger'}`}
                      disabled={m.role === 'super_admin'}
                      onClick={() => handleToggle(m)}
                    >
                      {m.is_active === false ? '恢復存取' : '停止存取'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editModules && (
        <div className="vp-modal-mask" onClick={(e) => { if (e.target === e.currentTarget) setEditModules(false); }}>
          <div className="vp-modal" role="dialog" aria-labelledby="modules-form-title">
            <div className="vp-modal-head">
              <h2 id="modules-form-title">編輯賣房／賣車功能</h2>
              <button type="button" className="vp-modal-x" onClick={() => setEditModules(false)} aria-label="關閉">✕</button>
            </div>
            <form onSubmit={saveModules}>
              <fieldset className="vp-module-pick">
                <legend>至少選擇一項</legend>
                <div className="vp-module-cards">
                  {MODULE_KEYS.map((key) => {
                    const on = modules.includes(key);
                    return (
                      <label key={key} className={`vp-module-card${on ? ' on' : ''}`}>
                        <input type="checkbox" checked={on} onChange={() => setModules(toggleModule(modules, key))} />
                        <span className="vp-module-card-title">{key === 'real_estate' ? '賣房' : '賣車'}</span>
                        <span className="vp-module-card-sub">{MODULE_LABEL[key]}</span>
                        <small>{MODULE_HINT[key]}</small>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
              <div className="vp-wiz-actions">
                <button type="button" className="vp-btn" onClick={() => setEditModules(false)}>取消</button>
                <button type="submit" className="vp-btn primary" disabled={saving}>{saving ? '處理中…' : '確認送出'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="vp-modal-mask" onClick={(e) => { if (e.target === e.currentTarget) setShowAdd(false); }}>
          <div className="vp-modal" role="dialog" aria-labelledby="add-member-title">
            <div className="vp-modal-head">
              <h2 id="add-member-title">加入成員</h2>
              <button type="button" className="vp-modal-x" onClick={() => setShowAdd(false)} aria-label="關閉">✕</button>
            </div>
            <form onSubmit={handleAdd}>
              <label className="vp-label">Email *
                <input className="vp-input" type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} required />
              </label>
              <label className="vp-label">姓名（新帳號必填建議）
                <input className="vp-input" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} maxLength={120} placeholder="例如：王小明" />
              </label>
              <label className="vp-label">初始密碼（帳號尚不存在時必填，至少 8 字）
                <input className="vp-input" type="password" autoComplete="new-password" value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} minLength={8} placeholder="已註冊帳號可留空" />
              </label>
              <label className="vp-label">公司角色
                <select className="vp-input" value={addForm.org_role} onChange={(e) => setAddForm({ ...addForm, org_role: e.target.value })}>
                  <option value="member">一般成員</option>
                  <option value="team_manager">團隊主管</option>
                  <option value="org_admin">公司管理員</option>
                </select>
              </label>
              <p className="vp-muted">不會由此授予平台管理員。帳號已存在時只加入本公司；不存在時會建立可登入帳號。</p>
              <div className="vp-wiz-actions">
                <button type="button" className="vp-btn" onClick={() => setShowAdd(false)}>取消</button>
                <button type="submit" className="vp-btn primary" disabled={saving}>{saving ? '處理中…' : '確認加入'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
